import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { ICategoriesResponse, ICategoryDropsResponse } from "@miplatform/types";
import { Accordion, Box } from "@mui/material";
import { IPriceLevelPayload } from "../PricingMatrix.types";
import { PRICE_LVL_COL_WIDTH } from "../constants/constants";
import { AccordionHeader } from "./AccordionHeader";
import { CategoryDropRows } from "./CategoryDropRows";
import { PriceLevelHeaderRow } from "./PriceLevelHeaderRow";

export function PriceMatrixTable({
  form,
  readOnly = false,
  priceLevels,
  groupedData,
  categoryDrops = [],
  openPriceLevelForm = false,
  activePriceLevelIndex = null,
  handleEditDropPrices = () => {},
  handleEditPriceLevel = () => {},
  handleSaveDropPrices = () => {},
  handleCancelPriceLevel = () => {},
  handleOpenDropTypeSelect = () => {},
  handleDeleteCategoryDrop = () => {},
  handleReorderPriceLevels = () => {},
  handleDuplicatePriceLevel = () => {},
}: PriceMatrixTableProps) {
  const [maxVisibleLevels, setMaxVisibleLevels] = useState(0);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);

  const { classes } = useStyles();

  const transform = `translateX(-${visibleStartIndex * PRICE_LVL_COL_WIDTH}px)`;

  return (
    <Box>
      <PriceLevelHeaderRow
        form={form}
        readOnly={readOnly}
        transform={transform}
        priceLevels={priceLevels}
        maxVisibleLevels={maxVisibleLevels}
        visibleStartIndex={visibleStartIndex}
        openPriceLevelForm={openPriceLevelForm}
        activePriceLevelIndex={activePriceLevelIndex}
        setMaxVisibleLevels={setMaxVisibleLevels}
        setVisibleStartIndex={setVisibleStartIndex}
        handleEditDropPrices={handleEditDropPrices}
        handleEditPriceLevel={handleEditPriceLevel}
        handleSaveDropPrices={handleSaveDropPrices}
        handleCancelPriceLevel={handleCancelPriceLevel}
        handleReorderPriceLevels={handleReorderPriceLevels}
        handleDuplicatePriceLevel={handleDuplicatePriceLevel}
      />
      {groupedData.map(({ category, categoryDrop }) => (
        <Accordion
          key={category.id}
          defaultExpanded
          disableGutters
          className={classes.accordion}
        >
          <AccordionHeader
            readOnly={readOnly}
            category={category}
            categoryDrop={categoryDrop}
            disabled={activePriceLevelIndex !== null}
            handleOpenDropTypeSelect={handleOpenDropTypeSelect}
          />
          <CategoryDropRows
            readOnly={readOnly}
            form={form}
            transform={transform}
            categoryId={category.id}
            categoryDrop={categoryDrop}
            categoryDrops={categoryDrops}
            priceLevels={priceLevels}
            activePriceLevelIndex={activePriceLevelIndex}
            handleDeleteCategoryDrop={handleDeleteCategoryDrop}
          />
        </Accordion>
      ))}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    marginBottom: theme.spacing(2),
  },
}));

interface PriceMatrixTableProps {
  priceLevels: IPriceLevelPayload[];
  groupedData: {
    category: ICategoriesResponse;
    categoryDrop: ICategoryDropsResponse[];
  }[];
  readOnly?: boolean;
  categoryDrops?: ICategoryDropsResponse[];
  openPriceLevelForm?: boolean;
  activePriceLevelIndex?: number | null;
  form?: UseFormReturn<IPriceLevelPayload>;
  handleEditDropPrices?: (level: IPriceLevelPayload, idx: number) => void;
  handleEditPriceLevel?: (level: IPriceLevelPayload) => void;
  handleSaveDropPrices?: () => void;
  handleCancelPriceLevel?: () => void;
  handleOpenDropTypeSelect?: (
    e: React.MouseEvent<HTMLElement>,
    categoryId: string,
    catDrops: ICategoryDropsResponse[]
  ) => void;
  handleDeleteCategoryDrop?: (
    categoryId: string,
    drop: Partial<ICategoryDropsResponse>
  ) => void;
  handleReorderPriceLevels?: (direction: "left" | "right", idx: number) => void;
  handleDuplicatePriceLevel?: (data: Partial<IPriceLevelPayload>) => void;
}
